/**
 * Supplier-RFQ status helpers.
 *
 * The source of truth for "has an RFQ been sent?" is
 * ``order.rfq_sends[]`` — a push-only array written by
 * ``POST /api/orders/{id}/production-purchase/rfq/send``. Each entry
 * carries ``status: 'sent' | 'failed'``, so a derived view can tell
 * confirmed sends from failed attempts without hitting the backend.
 *
 * The derivation lives in a shared helper so the Production Purchase
 * card, the Finance accordion header and the Supplier workflow
 * header all show the same thing — no risk of drift when we add
 * follow-up logic (inbound matching, expiry, etc.).
 */

export const RFQ_STATE = Object.freeze({
    NONE: 'none',                           // no successful send on record
    AWAITING_SUPPLIER_QUOTE: 'awaiting_supplier_quote', // at least one successful send
    RECEIVED: 'received',                   // admin explicitly marked quote received
});

export const RFQ_STATE_LABEL = Object.freeze({
    [RFQ_STATE.NONE]: null,
    [RFQ_STATE.AWAITING_SUPPLIER_QUOTE]: 'Awaiting Supplier Quote',
    [RFQ_STATE.RECEIVED]: 'Supplier Quote Received',
});

/**
 * Derive the supplier-RFQ status purely from the persisted
 * ``rfq_sends[]`` array. Returns:
 *
 *   {
 *     state,                     // RFQ_STATE enum
 *     label,                     // human-readable (null when NONE)
 *     latest,                    // most-recent successful send entry (or null)
 *     latest_any,                // most-recent entry regardless of status
 *     successful_count,          // count of status==='sent' entries
 *     failed_count,              // count of status==='failed' entries
 *     total_count,               // all entries
 *     all_sends,                 // the raw array, newest-last (as persisted)
 *   }
 *
 * We only promote the order to "Awaiting Supplier Quote" after at
 * least one ``status === 'sent'`` entry — failed sends do not count
 * toward the lifecycle. This matches the user requirement that an
 * RFQ be marked sent only after confirmed email-provider acceptance.
 */
export function deriveRfqStatus(order) {
    const sends = Array.isArray(order?.rfq_sends) ? order.rfq_sends : [];
    const successful = sends.filter(s => s && s.status === 'sent');
    const failed = sends.filter(s => s && s.status === 'failed');

    const latest = successful.length
        ? successful.reduce((a, b) =>
            new Date(a.sent_at || 0) > new Date(b.sent_at || 0) ? a : b)
        : null;

    const latest_any = sends.length
        ? sends.reduce((a, b) =>
            new Date(a.sent_at || 0) > new Date(b.sent_at || 0) ? a : b)
        : null;

    const state = latest
        ? (order?.rfq_received_at ? RFQ_STATE.RECEIVED : RFQ_STATE.AWAITING_SUPPLIER_QUOTE)
        : RFQ_STATE.NONE;
    return {
        state,
        label: RFQ_STATE_LABEL[state],
        latest,
        latest_any,
        successful_count: successful.length,
        failed_count: failed.length,
        total_count: sends.length,
        all_sends: sends,
        received_at: order?.rfq_received_at || null,
        received_by_user_name: order?.rfq_received_by_user_name || null,
        received_rfq_reference: order?.rfq_received_rfq_reference || null,
        received_version: order?.rfq_received_version ?? null,
        received_supplier_name: order?.rfq_received_supplier_name || null,
        received_po_reference: order?.rfq_received_po_reference || null,
    };
}

/**
 * True when a BOM line's unit price is genuinely unknown (i.e. the
 * supplier has not quoted yet) vs confirmed zero. In persistence
 * ``null`` ⇒ unknown, ``0`` ⇒ confirmed zero — see
 * ``routes/po_lines.ensure_po_lines_from_bom`` which stores
 * ``unit_price = None`` when the resolver returned no cost.
 */
export function isUnitPriceUnknown(line) {
    if (!line) return false;
    // Explicitly flagged as needing a quote.
    if (line.requires_quote === true) return true;
    const up = line.unit_price;
    // Explicit null/undefined — never set.
    if (up === null || up === undefined) return true;
    // Negative price is invalid → treat as unknown (matches backend).
    if (typeof up === 'number' && up < 0) return true;
    // Numeric 0 is a confirmed zero cost.
    return false;
}
